// SEO-optimized landing page for "2-Mann-Handling & Möbelmontage Hamburg".
// Targets: online-shop operators shipping large items (furniture / white goods),
// property managers coordinating move-ins/-outs, and private buyers of heavy
// consumer goods. Positions Talkani as the trusted 2-person delivery + assembly
// carrier for Northern Germany.
import { Link } from "react-router-dom";
import {
  Truck, Wrench, Users, ArrowRight, CheckCircle2, Clock, Phone, ShieldCheck,
  Package, Sofa, Wind, Refrigerator, MapPin, Camera, Layers, Recycle,
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import { BUSINESS_PROFILE } from "../data/seo";

// What we actually deliver + assemble — used both in copy and structured data.
const CARRY_ITEMS = [
  { icon: Sofa, label: "Sofas, Sessel, Betten" },
  { icon: Layers, label: "Küchen & Schränke" },
  { icon: Refrigerator, label: "Kühlschränke, Waschmaschinen" },
  { icon: Wind, label: "Trockner, Geschirrspüler" },
  { icon: Package, label: "Große TV-Geräte (65\"+)" },
  { icon: Wrench, label: "Fitness-Geräte, Massage-Sessel" },
];

// Transparent price tiers — headline anchors that convert scroll-readers.
const TIERS = [
  {
    name: "Standard",
    price: "59",
    unit: "€ ab",
    line: "2-Mann-Lieferung bis Wohnungstür",
    features: [
      "2 kräftige Träger",
      "Bis 4. OG ohne Aufzug",
      "Terminfenster 2 Std.",
      "Foto-Übergabe",
    ],
    testid: "tier-standard",
  },
  {
    name: "Handling+",
    price: "89",
    unit: "€ ab",
    highlighted: true,
    line: "Bis zum Aufstellungsort inkl. Auspacken",
    features: [
      "Alles aus Standard",
      "Aufstellen am Wunschort",
      "Auspacken vor Ort",
      "Verpackungsmitnahme",
    ],
    testid: "tier-handling-plus",
  },
  {
    name: "Full-Service",
    price: "149",
    unit: "€ ab",
    line: "Lieferung + Möbelmontage + Altgeräteentsorgung",
    features: [
      "Alles aus Handling+",
      "Möbelmontage nach Anleitung",
      "Anschluss (Waschmaschine, Kühlschr.)",
      "Altgeräteentsorgung (EAR-registriert)",
    ],
    testid: "tier-full-service",
  },
];

// Real questions online-shop logistics teams and property managers ask.
const FAQ = [
  {
    q: "Ab wie viel Kilogramm lohnt sich 2-Mann-Handling?",
    a: "Ab ~30 kg wird es für eine Person unhandlich — vor allem bei sperrigen Kartons durchs Treppenhaus. Für Waschmaschinen (~70 kg), Kühlschränke (~90 kg) und Sofas ist die 2-Personen-Variante ohnehin verpflichtend nach BGV D6 Arbeitsschutz.",
  },
  {
    q: "Bis in welches Stockwerk tragt ihr ohne Aufzug?",
    a: "Standardmäßig bis zum 4. OG. Ab dem 5. OG berechnen wir eine Etagenpauschale von €12/Stockwerk. Mit Aufzug (auch enger Personenaufzug) unabhängig von der Etage — sofern das Möbelstück hineinpasst.",
  },
  {
    q: "Wie schnell könnt ihr liefern?",
    a: "Standardlieferung 2–4 Werktage nach Auftrag. Express am nächsten Werktag ab €35 Zuschlag. Für Online-Shops mit Wartungsvertrag: fester Wochen-Slot, planbar bis 24 Std. vor Abholung.",
  },
  {
    q: "Was passiert, wenn ein Möbelstück beschädigt wird?",
    a: "Wir sind über die gesetzliche Verkehrshaftung (§ 425 HGB) bis €8,33/kg abgedeckt, für Möbel zusätzlich über eine Frachtführer-Haftpflichtversicherung. Bei Wertgegenständen empfehlen wir eine Zusatzdeckung — wir organisieren das kostenlos für Sie.",
  },
  {
    q: "Nehmt ihr Altgeräte mit?",
    a: "Ja. Bei Full-Service inklusive. Wir sind bei der Stiftung EAR registriert und entsorgen fachgerecht — Sie erhalten eine Entsorgungsbescheinigung, wichtig für Vermieter und Buchhaltung.",
  },
  {
    q: "Könnt ihr ganze Küchen liefern und aufbauen?",
    a: "Wir liefern und stellen auf. Vollständige Küchenmontage (Wasser, Strom, Ausrichtung) bieten wir mit einem festen Küchenbauer-Partner an — auf Anfrage im Full-Service-Paket.",
  },
];

// Advantages framed for the online-shop buyer persona (revenue-focused).
const BUYER_BENEFITS = [
  {
    icon: Users,
    title: "Weniger Retouren",
    text: "Beschädigungen sind die #1-Retourenquelle bei Möbeln (bis 12%). Unsere 2-Mann-Träger mit Möbelrutschen und Antirutsch-Handschuhen halten die Quote unter 1,5%.",
  },
  {
    icon: ShieldCheck,
    title: "5-Sterne-Bewertungen",
    text: "Kunden bewerten die Lieferung — nicht das Produkt. Mit Termin-SMS, freundlicher Übergabe und Aufbau vor Ort steigen Ihre Trustpilot- und Google-Ratings messbar.",
  },
  {
    icon: Recycle,
    title: "Nachhaltig verpacken",
    text: "Wir nehmen Karton, Styropor und Folie sofort mit und führen sie dem Recycling zu. Ihre Kunden schätzen die Sauberkeit — Sie sparen sich Beschwerden über liegen gelassenes Material.",
  },
  {
    icon: Camera,
    title: "Foto-Nachweis inklusive",
    text: "Jede Übergabe mit Datum, GPS und Foto dokumentiert. Bei Reklamationen haben Sie beweiskräftige Zustandsdokumentation — direkt im Admin-Dashboard herunterladbar.",
  },
];

export default function ZweimannHandling() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "2-Mann-Handling & Möbelmontage",
    provider: {
      "@type": "LocalBusiness",
      name: BUSINESS_PROFILE.name,
      telephone: BUSINESS_PROFILE.telephone,
      email: BUSINESS_PROFILE.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS_PROFILE.street,
        addressLocality: BUSINESS_PROFILE.city,
        postalCode: BUSINESS_PROFILE.postalCode,
        addressCountry: BUSINESS_PROFILE.country,
      },
    },
    areaServed: [
      { "@type": "City", name: "Hamburg" },
      { "@type": "City", name: "Bremen" },
      { "@type": "City", name: "Lübeck" },
      { "@type": "City", name: "Kiel" },
      { "@type": "City", name: "Hannover" },
    ],
    offers: [
      { "@type": "Offer", name: "Standard 2-Mann-Handling", priceCurrency: "EUR", price: "59" },
      { "@type": "Offer", name: "Handling+ (Auspacken)", priceCurrency: "EUR", price: "89" },
      { "@type": "Offer", name: "Full-Service (Montage + Entsorgung)", priceCurrency: "EUR", price: "149" },
    ],
    description:
      "2-Mann-Handling für Möbel, Großgeräte und schwere Sendungen in Norddeutschland. Inklusive Möbelmontage, Verpackungsmitnahme und Altgeräteentsorgung. Für Online-Shops, Hausverwaltungen und Privatkunden.",
  };

  return (
    <div data-testid="zweimann-page" lang="de">
      <SEOHead
        title="2-Mann-Handling & Möbelmontage Hamburg · ab €59 — Talkani"
        description="Zuverlässige 2-Mann-Lieferung für Sofas, Küchen, Waschmaschinen und Großgeräte in Hamburg & Norddeutschland. Inklusive Möbelmontage, Verpackungsmitnahme und Altgeräteentsorgung. Für Online-Shops und Privatkunden."
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* ===== HERO ===== */}
      <section className="relative bg-[#090A0B] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(/generated/umzug.png)" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#090A0B] via-[#090A0B]/95 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 text-[#FF5A00] mb-4">
            <Users size={16} />
            <span className="text-xs font-bold tracking-[0.3em] uppercase">2-Mann-Handling · Möbelmontage · Hamburg</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 leading-[0.95] max-w-4xl">
            Schwere Sendung?<br />Wir tragen. Wir bauen auf.
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed">
            2-Mann-Lieferung für Sofas, Kühlschränke, Küchen und Waschmaschinen — inklusive Möbelmontage,
            Verpackungsmitnahme und Altgeräteentsorgung. <strong className="text-white">Für Online-Shops,
            Hausverwaltungen und Privatkunden</strong> in Hamburg und ganz Norddeutschland.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/book?service=paket_lieferung&sub=2-Mann-Handling"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="zweimann-book-cta"
            >
              Jetzt buchen <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${BUSINESS_PROFILE.telephone.replace(/[\s-]/g, "")}`}
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="zweimann-call-cta"
            >
              <Phone size={16} /> Direkt anfragen
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-300">
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5A00]" /> Ab €59 pro Sendung</span>
            <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#FF5A00]" /> 2-4 Werktage · Express verfügbar</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#FF5A00]" /> Frachtführer-Haftpflicht</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#FF5A00]" /> Hamburg + Norddeutschland</span>
          </div>
        </div>
      </section>

      {/* ===== WAS WIR TRAGEN ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Was wir liefern & aufbauen</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-10 max-w-3xl">
            Alles, was zu schwer für eine Person ist.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CARRY_ITEMS.map(({ icon: Icon, label }) => (
              <div key={label} className="bg-neutral-50 border border-neutral-200 p-5 hover:border-[#FF5A00] transition-colors flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 bg-[#FF5A00]/10 text-[#FF5A00] flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <span className="text-sm font-bold">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICE TIERS ===== */}
      <section className="py-20 lg:py-24 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Transparente Pakete</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-4 max-w-3xl">
            Vom Tragen bis zur fertigen Aufstellung — Sie wählen.
          </h2>
          <p className="text-neutral-600 mb-10 max-w-2xl">
            Alle Preise gelten für Hamburg-Stadt. Umland +€0,35/km. Online-Shops mit Rahmenvertrag: bis 20% Volumenrabatt.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`p-8 border transition-all ${
                  tier.highlighted
                    ? "bg-[#090A0B] text-white border-[#090A0B] shadow-2xl scale-[1.02]"
                    : "bg-white border-neutral-200"
                }`}
                data-testid={tier.testid}
              >
                {tier.highlighted && (
                  <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-4">
                    ★ Beliebteste Wahl
                  </div>
                )}
                <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${tier.highlighted ? "text-neutral-400" : "text-neutral-500"}`}>
                  {tier.name}
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className={`text-xs ${tier.highlighted ? "text-neutral-400" : "text-neutral-500"}`}>{tier.unit}</span>
                  <span className="text-5xl font-black tracking-tighter">{tier.price}</span>
                  <span className="text-lg font-bold">€</span>
                </div>
                <p className={`text-sm mb-6 leading-relaxed ${tier.highlighted ? "text-neutral-300" : "text-neutral-600"}`}>
                  {tier.line}
                </p>
                <ul className="space-y-2.5 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={16} className={`mt-0.5 flex-shrink-0 ${tier.highlighted ? "text-[#FF5A00]" : "text-emerald-600"}`} />
                      <span className={tier.highlighted ? "text-neutral-200" : "text-neutral-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/book?service=paket_lieferung&sub=${encodeURIComponent(tier.name)}`}
                  className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 font-bold uppercase tracking-wider text-xs transition-colors ${
                    tier.highlighted
                      ? "bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                      : "bg-neutral-100 hover:bg-neutral-200 text-neutral-900"
                  }`}
                  data-testid={`${tier.testid}-book`}
                >
                  {tier.name} buchen <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BENEFITS FOR ONLINE SHOPS ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Für Online-Shops</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-10 max-w-3xl">
            Ihre Kunden bewerten die Lieferung — nicht das Produkt.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {BUYER_BENEFITS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-neutral-50 border border-neutral-200 p-6 hover:border-[#FF5A00] transition-colors">
                <div className="w-10 h-10 bg-[#FF5A00]/10 text-[#FF5A00] flex items-center justify-center mb-4">
                  <Icon size={18} />
                </div>
                <h3 className="text-base font-black tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-20 lg:py-24 bg-[#090A0B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Ablauf</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-12 max-w-3xl">
            Vier Schritte. Ihre Sendung — perfekt zugestellt.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Abholung beim Absender", d: "Werk, Zentrallager oder Online-Shop-Rampe — wir kommen im vereinbarten Zeitfenster." },
              { n: "02", t: "Termin-SMS an Endkunden", d: "48 Std. vor Zustellung erhält der Empfänger einen genauen 2-Std.-Slot mit Live-Tracking-Link." },
              { n: "03", t: "2-Mann-Zustellung", d: "Wir tragen bis zum Aufstellungsort. Rutschmatten schützen Böden, Ecken werden abgeklebt." },
              { n: "04", t: "Aufbau + Fotoprotokoll", d: "Möbelmontage, Verpackung mitnehmen, Foto-Übergabe im System — Rechnung geht am Monatsende raus." },
            ].map(({ n, t, d }) => (
              <div key={n} className="bg-white/5 border border-white/10 p-6">
                <div className="text-[#FF5A00] font-black text-3xl tracking-tighter mb-3">{n}</div>
                <div className="text-base font-black tracking-tight mb-2">{t}</div>
                <p className="text-sm text-neutral-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Häufige Fragen</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-10">
            Alles, was Sie vor dem Buchen wissen wollen.
          </h2>
          <div className="space-y-3">
            {FAQ.map(({ q, a }, i) => (
              <details
                key={q}
                className="group border border-neutral-200 bg-neutral-50 open:bg-white open:border-[#FF5A00]/40"
                data-testid={`zweimann-faq-${i}`}
              >
                <summary className="p-5 cursor-pointer list-none font-bold flex items-center justify-between gap-4">
                  <span>{q}</span>
                  <span className="text-[#FF5A00] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="px-5 pb-5 text-sm text-neutral-700 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FOOTER ===== */}
      <section className="py-20 lg:py-24 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Truck size={40} className="text-[#FF5A00] mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
            Bereit für zuverlässige Zustellung mit Extra-Service?
          </h2>
          <p className="text-neutral-600 mb-8 max-w-2xl mx-auto">
            Ob eine Küche für Ihren Kunden oder wöchentliche Möbellieferungen als Online-Shop — wir bringen es sicher hin,
            bauen es auf und nehmen die Verpackung mit. Sprechen Sie mit uns.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/book?service=paket_lieferung&sub=2-Mann-Handling"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition inline-flex items-center gap-2"
              data-testid="zweimann-footer-book"
            >
              Sendung buchen <ArrowRight size={16} />
            </Link>
            <Link
              to="/#b2b-lead"
              className="bg-white border border-neutral-300 hover:border-[#FF5A00] text-neutral-900 font-bold uppercase tracking-wide px-7 py-4 transition inline-flex items-center gap-2"
              data-testid="zweimann-footer-b2b"
            >
              Als Online-Shop anfragen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
